/**
 * Stream C1 + C2 (#2785, #2786) — Connections graph node-label sanitisation.
 *
 * The graph's `data.label` field comes straight from the API (which in turn
 * gets it from extracted document text, journal titles, and entity tags).
 * Real-world inputs include:
 *   * Mojibake (e.g. "Ã©cole" instead of "école")
 *   * Pure noise — control characters, replacement glyph U+FFFD
 *   * Foreign-language text (Cyrillic, CJK, Arabic) — display as-is, never strip
 *   * Very long unbroken strings — truncate
 *
 * `sanitizeNodeLabel` returns a label that is safe to render in the cytoscape
 * graph. If the input is unrecoverable, it falls back to `fallback`
 * (default: "Untitled").
 */

const MOJIBAKE_REPLACEMENTS: Array<[RegExp, string]> = [
  [/Ã©/g, 'é'],
  [/Ã¨/g, 'è'],
  [/Ã /g, 'à'],
  [/Ã¢/g, 'â'],
  [/Ã®/g, 'î'],
  [/Ã´/g, 'ô'],
  [/Ã¹/g, 'ù'],
  [/Ã»/g, 'û'],
  [/Ã§/g, 'ç'],
  [/Ã±/g, 'ñ'],
  [/Ã¼/g, 'ü'],
  [/Ã¶/g, 'ö'],
  [/Ã¤/g, 'ä'],
  [/Ã¡/g, 'á'],
  [/Ã­/g, 'í'],
  [/Ã³/g, 'ó'],
  [/Ã¯/g, 'ï'],
  [/Â /g, ' '],
  [/Â/g, ''],
]

// Control chars (except space + tab + newline) and the Unicode replacement glyph.
// eslint-disable-next-line no-control-regex
const CONTROL_AND_NOISE_RE = /[\u0000-\u0008\u000B-\u001F\u007F\uFFFD]/g

/**
 * Returns true if the string is recognisably "noise" — too many non-letter
 * runs, or fewer than 2 letter chars after cleaning. Foreign scripts
 * (Cyrillic, CJK, Arabic, etc.) MUST pass this check.
 */
function looksLikeNoise(s: string): boolean {
  if (!s) return true
  // Treat any Unicode letter as a "letter" — \p{L} covers all scripts.
  const letters = s.match(/\p{L}/gu)
  if (!letters || letters.length < 2) return true
  // If 60%+ of the chars are non-letter and non-space symbols/punct, treat as noise.
  const nonLetter = s.replace(/\p{L}|\p{N}|\s/gu, '')
  return nonLetter.length / s.length > 0.6
}

export function sanitizeNodeLabel(input: unknown, fallback = 'Untitled'): string {
  if (input == null) return fallback
  let s = String(input)

  // Strip control characters & U+FFFD replacement glyph.
  s = s.replace(CONTROL_AND_NOISE_RE, '')

  // Try mojibake replacement (cheap; safe — only fires on known patterns).
  for (const [re, rep] of MOJIBAKE_REPLACEMENTS) {
    s = s.replace(re, rep)
  }

  // Collapse runs of whitespace.
  s = s.replace(/\s+/g, ' ').trim()

  if (!s) return fallback
  if (looksLikeNoise(s)) return fallback

  // Truncate very long unbroken strings at a word boundary, NO trailing `…`.
  // CoWork Round 4 finding: the previous '…' suffix here was the source of
  // the trailing-ellipsis on graph labels CoWork failed in R4 Test 2d. The
  // cytoscape graph then has `text-wrap: 'wrap'` + `text-max-width: 140px`
  // which wraps the truncated text onto multiple lines naturally — no `…`
  // glyph anywhere on screen.
  if (s.length > 60) {
    let truncated = s.slice(0, 60)
    const lastSpace = truncated.lastIndexOf(' ')
    if (lastSpace > 30) {
      // Trim back to the last word boundary, but only if it isn't too far back.
      truncated = truncated.slice(0, lastSpace)
    }
    s = truncated.trimEnd()
  }
  return s
}
